import { supabase } from '../lib/supabase';

/**
 * Review Criteria Service
 * Handles custom review evaluation schema operations
 * Converts between snake_case (database) and camelCase (React)
 */

export const reviewCriteriaService = {
  /**
   * Get all review criteria for a conference
   */
  async getByConference(conferenceId) {
    try {
      const { data, error } = await supabase?.from('review_criteria')?.select('*')?.eq('conference_id', conferenceId)?.order('display_order', { ascending: true });

      if (error) throw error;

      return data?.map(criteria => ({
        id: criteria?.id,
        conferenceId: criteria?.conference_id,
        questionText: criteria?.question_text,
        questionType: criteria?.question_type,
        helpText: criteria?.help_text,
        isRequired: criteria?.is_required,
        displayOrder: criteria?.display_order,
        scaleMin: criteria?.scale_min,
        scaleMax: criteria?.scale_max,
        scaleMinLabel: criteria?.scale_min_label,
        scaleMaxLabel: criteria?.scale_max_label,
        options: criteria?.options,
        createdAt: criteria?.created_at,
        updatedAt: criteria?.updated_at
      })) || [];
    } catch (error) {
      console.error('Get review criteria error:', error);
      throw error;
    }
  },

  /**
   * Create review criteria for a conference
   */
  async create(criteriaData) {
    try {
      const { data, error } = await supabase?.from('review_criteria')?.insert({
          conference_id: criteriaData?.conferenceId,
          question_text: criteriaData?.questionText,
          question_type: criteriaData?.questionType,
          help_text: criteriaData?.helpText,
          is_required: criteriaData?.isRequired !== false,
          display_order: criteriaData?.displayOrder || 0,
          scale_min: criteriaData?.scaleMin,
          scale_max: criteriaData?.scaleMax,
          scale_min_label: criteriaData?.scaleMinLabel,
          scale_max_label: criteriaData?.scaleMaxLabel,
          options: criteriaData?.options || []
        })?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        conferenceId: data?.conference_id,
        questionText: data?.question_text,
        questionType: data?.question_type,
        helpText: data?.help_text,
        isRequired: data?.is_required,
        displayOrder: data?.display_order,
        scaleMin: data?.scale_min,
        scaleMax: data?.scale_max,
        scaleMinLabel: data?.scale_min_label,
        scaleMaxLabel: data?.scale_max_label,
        options: data?.options
      };
    } catch (error) {
      console.error('Create review criteria error:', error);
      throw error;
    }
  },

  /**
   * Update review criteria
   */
  async update(criteriaId, updates) {
    try {
      const updateData = {};
      if (updates?.questionText !== undefined) updateData.question_text = updates?.questionText;
      if (updates?.questionType !== undefined) updateData.question_type = updates?.questionType;
      if (updates?.helpText !== undefined) updateData.help_text = updates?.helpText;
      if (updates?.isRequired !== undefined) updateData.is_required = updates?.isRequired;
      if (updates?.displayOrder !== undefined) updateData.display_order = updates?.displayOrder;
      if (updates?.scaleMin !== undefined) updateData.scale_min = updates?.scaleMin;
      if (updates?.scaleMax !== undefined) updateData.scale_max = updates?.scaleMax;
      if (updates?.scaleMinLabel !== undefined) updateData.scale_min_label = updates?.scaleMinLabel;
      if (updates?.scaleMaxLabel !== undefined) updateData.scale_max_label = updates?.scaleMaxLabel;
      if (updates?.options !== undefined) updateData.options = updates?.options;

      const { data, error } = await supabase?.from('review_criteria')?.update(updateData)?.eq('id', criteriaId)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Update review criteria error:', error);
      throw error;
    }
  },

  /**
   * Delete review criteria
   */
  async delete(criteriaId) {
    try {
      const { error } = await supabase?.from('review_criteria')?.delete()?.eq('id', criteriaId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete review criteria error:', error);
      throw error;
    }
  },

  /**
   * Bulk create review criteria
   */
  async bulkCreate(conferenceId, criteriaArray) {
    try {
      const insertData = criteriaArray?.map((criteria, index) => ({
        conference_id: conferenceId,
        question_text: criteria?.questionText,
        question_type: criteria?.questionType,
        help_text: criteria?.helpText,
        is_required: criteria?.isRequired !== false,
        display_order: criteria?.displayOrder !== undefined ? criteria?.displayOrder : index,
        scale_min: criteria?.scaleMin,
        scale_max: criteria?.scaleMax,
        scale_min_label: criteria?.scaleMinLabel,
        scale_max_label: criteria?.scaleMaxLabel,
        options: criteria?.options || []
      }));

      const { data, error } = await supabase?.from('review_criteria')?.insert(insertData)?.select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Bulk create review criteria error:', error);
      throw error;
    }
  },

  /**
   * Save review answers
   */
  async saveAnswers(submissionId, reviewerId, answers) {
    try {
      const insertData = answers?.map(answer => {
        const data = {
          submission_id: submissionId,
          reviewer_id: reviewerId,
          criteria_id: answer?.criteriaId
        };

        // Store answer in appropriate column based on type
        if (answer?.questionType === 'scale') {
          data.answer_numeric = answer?.value;
        } else if (answer?.questionType === 'true_false') {
          data.answer_boolean = answer?.value;
        } else {
          data.answer_text = answer?.value;
        }

        return data;
      });

      const { data, error } = await supabase?.from('review_answers')?.upsert(insertData, { onConflict: 'submission_id,reviewer_id,criteria_id' })?.select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Save review answers error:', error);
      throw error;
    }
  },

  /**
   * Get review answers for a submission by a reviewer
   */
  async getAnswers(submissionId, reviewerId) {
    try {
      const { data, error } = await supabase?.from('review_answers')?.select('*')?.eq('submission_id', submissionId)?.eq('reviewer_id', reviewerId);

      if (error) throw error;

      return data?.map(answer => ({
        id: answer?.id,
        submissionId: answer?.submission_id,
        reviewerId: answer?.reviewer_id,
        criteriaId: answer?.criteria_id,
        answerNumeric: answer?.answer_numeric,
        answerBoolean: answer?.answer_boolean,
        answerText: answer?.answer_text,
        createdAt: answer?.created_at,
        updatedAt: answer?.updated_at
      })) || [];
    } catch (error) {
      console.error('Get review answers error:', error);
      throw error;
    }
  },

  /**
   * Delete all review criteria for a conference
   */
  async deleteByConference(conferenceId) {
    try {
      const { error } = await supabase
        ?.from('review_criteria')
        ?.delete()
        ?.eq('conference_id', conferenceId);

      if (error) throw error;
    } catch (error) {
      console.error('Delete criteria by conference error:', error);
      throw error;
    }
  },

  /**
   * Create multiple review criteria (alias for bulkCreate)
   */
  async createMultiple(conferenceId, criteriaArray) {
    return this.bulkCreate(conferenceId, criteriaArray);
  },

  /**
   * Get conference review statistics
   */
  async getConferenceReviewStats(conferenceId) {
    try {
      // Get total submissions
      const { count: totalSubmissions, error: submissionsError } = await supabase?.from('conference_submissions')?.select('*', { count: 'exact', head: true })?.eq('conference_id', conferenceId)?.eq('status', 'submitted');

      if (submissionsError) throw submissionsError;

      // Get submissions with reviews
      const { data: reviewData, error: reviewError } = await supabase?.from('submission_reviews')?.select('submission_id')?.eq('conference_id', conferenceId)?.eq('is_draft', false);

      if (reviewError) throw reviewError;

      const submissionsWithReviews = new Set(reviewData?.map(r => r?.submission_id))?.size || 0;

      return {
        totalSubmissions: totalSubmissions || 0,
        reviewedSubmissions: submissionsWithReviews,
        pendingReviews: (totalSubmissions || 0) - submissionsWithReviews,
        completionRate: totalSubmissions > 0 ? Math.round((submissionsWithReviews / totalSubmissions) * 100) : 0
      };
    } catch (error) {
      console.error('Get conference review stats error:', error);
      throw error;
    }
  }
};

export default reviewCriteriaService;